import { FormGroup, TextArea } from '@ksyos/design-system';
import { useEffect } from 'react';
import type { IService } from '@common/interfaces';
import type { ITocRequestData } from '@common/validation/customControls/ophthalmology';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { PractitionerRoleInRequest } from '@common/enums';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils';
import type { ICustomControlProcedureAddEditProps } from '../../interfaces';
import type { ProcessKey } from '../../utils/v1/transformValidationErrors';
import { NewOrEditRequestProcessContainer, ServiceOrganizationProvider } from '../../utils';
import { transformValidationErrors } from '../../utils/v1/transformValidationErrors';

type ITooV1TocProcedureDianosticReportAddEditProps = ICustomControlProcedureAddEditProps & {
    children: JSX.Element;
    tocService: IService;
};

const errorKeysToProcess: ProcessKey[] = [
    {
        checkKey: 'customControlData.consult.selectedDiagnosticReportProviderPractitionerId',
        targetKey: 'selectedDiagnosticReportProviderPractitionerId',
    },
    {
        checkKey: 'customControlData.consult.selectedDiagnosticReportProviderOrganizationId',
        targetKey: 'selectedDiagnosticReportProviderOrganizationId',
    },
];

export function TooV1TocProcedureDiagnosticReportAddEdit(props: ITooV1TocProcedureDianosticReportAddEditProps) {
    const consultData: ITocRequestData = props.procedure.customControlData.consult;
    const translation = getSelectedLanguage();

    useEffect(() => {
        if (!consultData) {
            props.handleChangeCustomControl({
                consult: {
                    evaluation: {
                        otherwiseReferPatient: null,
                    },
                    question: null,
                    selectedDiagnosticReportProviderOrganizationId: null,
                    selectedDiagnosticReportProviderPractitionerId: null,
                    toelichting: null,
                },
            });
            props.validateProcedure('draft');
        }
    }, [consultData]);

    return (
        <NewOrEditRequestProcessContainer
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            process={props.process}
            service={props.service}
            title={getServiceLabel(props.tocService.code, 'request', props.tocService.name)}
        >
            <ServiceOrganizationProvider
                errors={transformValidationErrors(props.errors, errorKeysToProcess)}
                initialOrganizationId={consultData?.selectedDiagnosticReportProviderOrganizationId || null}
                initialPractitionerId={consultData?.selectedDiagnosticReportProviderPractitionerId || null}
                onChange={handleChangeAndValidateServiceOrganization}
                onDoValidate={handleValidateServiceOrganizationProvider}
                roleOfPractitionersInList={PractitionerRoleInRequest.specialist}
                searchInNetwork={true}
                serviceProviderServiceId={props.tocService.id}
            />
            <FormGroup
                hasErrors={props.errors.has('customControlData.consult.question')}
                isRequired={true}
                label={translation.request.question}
            >
                <TextArea
                    data-testid="consult-vraag"
                    error={getValidationErrorTranslation(props.errors.get('customControlData.consult.question'))}
                    onChange={async (event) => {
                        await handleChangeAndValidate('question', event.target.value);
                    }}
                    placeholder={`${translation.request.question}...`}
                    rows={1}
                    value={consultData?.question || ''}
                />
            </FormGroup>
            <FormGroup
                hasErrors={props.errors.has('customControlData.consult.toelichting')}
                label={translation.diagnosticReport.remarks}
            >
                <TextArea
                    data-testid="consult-toelichting"
                    error={getValidationErrorTranslation(props.errors.get('customControlData.consult.toelichting'))}
                    onChange={async (event) => await handleChangeAndValidate('toelichting', event.target.value)}
                    placeholder={`${translation.diagnosticReport.remarks}...`}
                    rows={1}
                    value={consultData?.toelichting || ''}
                />
            </FormGroup>
            {props.children}
        </NewOrEditRequestProcessContainer>
    );

    async function handleValidateServiceOrganizationProvider() {
        await props.validateOneCustomControl(`consult.selectedDiagnosticReportProviderOrganizationId`);
    }

    async function handleChangeAndValidateServiceOrganization(
        organizationId: string | null,
        practitionerId: string | null
    ) {
        const newData = {
            ...props.procedure.customControlData.consult,
            selectedDiagnosticReportProviderOrganizationId: organizationId,
            selectedDiagnosticReportProviderPractitionerId: practitionerId,
        };

        await props.handleChangeCustomControl({
            consult: newData,
        });
    }

    async function handleChangeAndValidate<K extends keyof ITocRequestData>(name: K, value: string) {
        await props.handleChangeCustomControl({
            consult: {
                ...props.procedure.customControlData.consult,
                [name]: value ?? null,
            },
        });
        await props.validateOneCustomControl(`consult.${name}`);
    }
}
